import {
    useEffect,
    useMemo,
    useState,
} from "react";

import DataTable from "components/table/DataTable";

import {
    showError,
    showSuccess,
} from "utils/toast";

import {
    getStatements,
    downloadStatement,
} from "services/api/reportsApi";

import {
    statementColumns,
} from "./statementColumns";


const DEFAULT_FILTERS = {
    fromDate: "",
    toDate: "",
    employeeId: "",
    schoolId: "",
    partyId: "",
    status: "",
};


export default function StatementHistory() {

    const [
        statements,
        setStatements,
    ] = useState([]);


    const [
        loading,
        setLoading,
    ] = useState(false);


    const [
        filters,
        setFilters,
    ] = useState(
        DEFAULT_FILTERS
    );


    const [
        pagination,
        setPagination,
    ] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
    });


    /*
    |--------------------------------------------------------------------------
    | LOAD
    |--------------------------------------------------------------------------
    */

    const loadStatements = async (
        selectedPage = 1
    ) => {

        try {

            setLoading(
                true
            );


            const params = {
                page:
                    selectedPage,

                limit:
                    pagination.limit,

                ...filters,
            };


            Object.keys(
                params
            ).forEach(
                (key) => {

                    if (
                        params[key] === "" ||
                        params[key] ===
                            undefined ||
                        params[key] ===
                            null
                    ) {

                        delete params[
                            key
                        ];

                    }

                }
            );


            const response =
                await getStatements(
                    params
                );


            const data =
                response?.data ||
                response;


            const result =
                data?.data ||
                {};


            setStatements(
                Array.isArray(
                    result.statements
                )
                    ? result.statements
                    : []
            );


            setPagination(
                result.pagination ||
                {
                    page:
                        selectedPage,

                    limit:
                        pagination.limit,

                    total: 0,

                    totalPages: 0,
                }
            );

        } catch (
            error
        ) {

            console.error(
                "Statement history error:",
                error
            );


            setStatements(
                []
            );


            showError(
                error?.response?.data?.message ||
                "Unable to load statement history."
            );

        } finally {

            setLoading(
                false
            );

        }

    };


    useEffect(
        () => {

            loadStatements(
                1
            );

        },
        []
    );


    /*
    |--------------------------------------------------------------------------
    | FILTER CHANGE
    |--------------------------------------------------------------------------
    */

    const handleChange = (
        e
    ) => {

        const {
            name,
            value,
        } = e.target;


        setFilters(
            (prev) => ({
                ...prev,
                [name]: value,
            })
        );
    };


    /*
    |--------------------------------------------------------------------------
    | RESET
    |--------------------------------------------------------------------------
    */

    const handleReset = () => {

        setFilters(
            DEFAULT_FILTERS
        );

        setTimeout(
            () =>
                loadStatements(
                    1
                ),
            0
        );

    };


    /*
    |--------------------------------------------------------------------------
    | DOWNLOAD
    |--------------------------------------------------------------------------
    */

    const handleDownload = async (
        statement
    ) => {

        try {

            const response =
                await downloadStatement(
                    statement.id
                );


            const blob =
                new Blob([
                    response.data,
                ]);


            const url =
                window.URL.createObjectURL(
                    blob
                );


            const link =
                document.createElement(
                    "a"
                );


            link.href =
                url;


            link.download =
                statement.fileName ||
                `statement-${statement.id}.${statement.format}`;


            document.body.appendChild(
                link
            );


            link.click();


            link.remove();


            window.URL.revokeObjectURL(
                url
            );


            showSuccess(
                "Statement downloaded successfully."
            );

        } catch (
            error
        ) {

            console.error(
                "Statement download error:",
                error
            );


            showError(
                error?.response?.data?.message ||
                "Unable to download statement."
            );

        }

    };


    /*
    |--------------------------------------------------------------------------
    | COLUMNS
    |--------------------------------------------------------------------------
    */

    const columns =
        useMemo(
            () =>
                statementColumns({
                    onView:
                        (statement) =>
                            handleDownload(
                                statement
                            ),

                    onDownload:
                        handleDownload,
                }),
            []
        );


    return (

        <div className="mt-6">

            {/* =========================================================
                HEADER
            ========================================================= */}

            <div
                className="
                    mb-4
                    flex
                    items-center
                    justify-between
                "
            >

                <div>

                    <h2
                        className="
                            text-xl
                            font-bold
                            text-navy-700
                        "
                    >
                        Statement History
                    </h2>

                    <p
                        className="
                            mt-1
                            text-sm
                            text-gray-500
                        "
                    >
                        View and download generated
                        statements.
                    </p>

                </div>

            </div>


            {/* =========================================================
                FILTERS
            ========================================================= */}

            <div
                className="
                    mb-5
                    rounded-xl
                    border
                    border-gray-200
                    bg-white
                    p-5
                    shadow-sm
                "
            >

                <div
                    className="
                        grid
                        grid-cols-1
                        gap-4
                        md:grid-cols-3
                        lg:grid-cols-6
                    "
                >

                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            From Date
                        </label>

                        <input
                            type="date"
                            name="fromDate"
                            value={
                                filters.fromDate
                            }
                            onChange={
                                handleChange
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        />
                    </div>


                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            To Date
                        </label>

                        <input
                            type="date"
                            name="toDate"
                            value={
                                filters.toDate
                            }
                            onChange={
                                handleChange
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        />
                    </div>


                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            Employee ID
                        </label>

                        <input
                            type="text"
                            name="employeeId"
                            value={
                                filters.employeeId
                            }
                            onChange={
                                handleChange
                            }
                            placeholder="Employee ID"
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        />
                    </div>


                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            School ID
                        </label>

                        <input
                            type="text"
                            name="schoolId"
                            value={
                                filters.schoolId
                            }
                            onChange={
                                handleChange
                            }
                            placeholder="School ID"
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        />
                    </div>


                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            Party ID
                        </label>

                        <input
                            type="text"
                            name="partyId"
                            value={
                                filters.partyId
                            }
                            onChange={
                                handleChange
                            }
                            placeholder="Party ID"
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        />
                    </div>


                    <div>
                        <label
                            className="
                                mb-1
                                block
                                text-xs
                                font-semibold
                                text-gray-600
                            "
                        >
                            Status
                        </label>

                        <select
                            name="status"
                            value={
                                filters.status
                            }
                            onChange={
                                handleChange
                            }
                            className="
                                w-full
                                rounded-lg
                                border
                                border-gray-300
                                bg-white
                                px-3
                                py-2.5
                                text-sm
                                outline-none
                                focus:border-brand-500
                                focus:ring-2
                                focus:ring-brand-100
                            "
                        >

                            <option value="">
                                All Statuses
                            </option>

                            <option value="generated">
                                Generated
                            </option>

                            <option value="generating">
                                Generating
                            </option>

                            <option value="failed">
                                Failed
                            </option>

                        </select>
                    </div>

                </div>


                <div
                    className="
                        mt-4
                        flex
                        items-center
                        gap-3
                    "
                >

                    <button
                        type="button"
                        onClick={() =>
                            loadStatements(
                                1
                            )
                        }
                        disabled={
                            loading
                        }
                        className="
                            rounded-lg
                            bg-brand-500
                            px-5
                            py-2.5
                            text-sm
                            font-semibold
                            text-white
                            hover:bg-brand-600
                            disabled:opacity-50
                        "
                    >
                        {
                            loading
                                ? "Loading..."
                                : "Apply Filters"
                        }
                    </button>


                    <button
                        type="button"
                        onClick={
                            handleReset
                        }
                        disabled={
                            loading
                        }
                        className="
                            rounded-lg
                            border
                            border-gray-300
                            bg-white
                            px-5
                            py-2.5
                            text-sm
                            font-semibold
                            text-gray-700
                            hover:bg-gray-50
                            disabled:opacity-50
                        "
                    >
                        Reset
                    </button>

                </div>

            </div>


            {/* =========================================================
                TABLE
            ========================================================= */}

            <DataTable

                title="Statement History"

                columns={
                    columns
                }

                data={
                    statements
                }

                loading={
                    loading
                }

                addButtonText=""
            />

        </div>

    );
}